// src/components/materials/CreateMaterialDialog.tsx
//
// Commissioning a material from a CRM record.
//
// ============================================================================
// THIS SPENDS THE WORKSPACE'S MONEY
// ============================================================================
//
// Generation is billable (`pitchless.artifact.generate`). Two things follow,
// and both are load-bearing:
//
//   1. The server derives the idempotency key from the RECORD and the TYPE,
//      not from this component. A double-click, a retry after a timeout, or a
//      remounted dialog all produce the same key and therefore one artifact
//      and one charge. The disabled button below is a courtesy, not the
//      guarantee - it can fail and must not be relied on.
//
//   2. Asking for a SECOND material of the same type from the same record is
//      a legitimate thing to want, but it must be a deliberate act. The
//      confirmation step below exists for exactly that, and it is the only
//      path that sends an explicit idempotency key.
//
// The type list comes from PitchLess. CRM keeps no copy: a hardcoded list here
// would drift the day someone adds a template, and the failure mode is a user
// asking for a type that no longer exists.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useGetMaterialTypesQuery,
  useCreateMaterialMutation,
  materialErrorCode,
  type MaterialSurface,
  normaliseMaterialTypes,
} from '../../api/materialsApi';

export interface CreateMaterialDialogProps {
  entityType: MaterialSurface;
  entityId: string;
  /** What the user calls this record, for the heading. */
  entityLabel: string;
  /** Types already generated from this record, so a repeat can be flagged. */
  existingTypeKeys?: string[];
  onClose: () => void;
  onCreated?: (materialId: string | undefined) => void;
}


export function CreateMaterialDialog({
  entityType,
  entityId,
  entityLabel,
  existingTypeKeys = [],
  onClose,
  onCreated,
}: CreateMaterialDialogProps): React.ReactElement {
  const { data, isLoading, isError } = useGetMaterialTypesQuery();
  const [create, { isLoading: creating }] = useCreateMaterialMutation();

  const [typeKey, setTypeKey] = useState('');
  const [title, setTitle] = useState('');
  const [brief, setBrief] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [confirmDuplicate, setConfirmDuplicate] = useState(false);

  const types = useMemo(() => normaliseMaterialTypes(data), [data]);

  // FOCUS MANAGEMENT.
  //
  // A dialog that opens without moving focus leaves a keyboard user still on
  // the page behind it, tabbing through content they cannot see. One that
  // closes without restoring focus drops them at the top of the document,
  // with no way back to where they were — which the accessibility sweep
  // caught here: focus landed on <body> after Cancel.
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    openerRef.current = (document.activeElement as HTMLElement | null) ?? null;
    // Move focus INTO the dialog, on the first thing worth acting on.
    const first = dialogRef.current?.querySelector<HTMLElement>(
      'select, input, textarea, button'
    );
    first?.focus();

    return () => {
      // Back where they came from, if it is still there to return to.
      const opener = openerRef.current;
      if (opener && document.contains(opener)) opener.focus();
    };
  }, []);

  // Escape closes, as a modal must.
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    },
    [onClose]
  );
  const isDuplicate = typeKey !== '' && existingTypeKeys.includes(typeKey);

  const submit = async (deliberateSecondCopy: boolean): Promise<void> => {
    setError(null);
    try {
      const result = await create({
        entityType,
        entityId,
        artifactTypeKey: typeKey,
        title: title.trim() === '' ? undefined : title.trim(),
        // Left blank, the server composes a brief from the record. PitchLess
        // refuses an empty one, so something always has to be sent — but the
        // user is not made to write it.
        instructions: brief.trim() === '' ? undefined : brief.trim(),
        // ONLY here. Without it the server's record-derived key makes a repeat
        // return the existing material instead of creating (and charging for)
        // a second one - which is the correct default.
        ...(deliberateSecondCopy
          ? { idempotencyKey: `crm:${entityType}:${entityId}:${typeKey}:${Date.now()}` }
          : {}),
      }).unwrap();
      onCreated?.((result as { id?: string } | undefined)?.id);
      onClose();
    } catch (e) {
      const code = materialErrorCode(e);
      setError(
        code === 'not_configured'
          ? 'PitchLess is not connected in this environment. This is a setup issue, not something you can retry.'
          : code === 'forbidden'
            ? 'You do not have access to create materials in PitchLess. Ask a workspace administrator.'
            : 'Could not start generation. Please try again.'
      );
      setConfirmDuplicate(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-material-title"
      onKeyDown={onKeyDown}
    >
      <div
        ref={dialogRef}
        data-testid="create-material-dialog"
        className="bg-white w-full sm:max-w-lg rounded-t-xl sm:rounded-xl shadow-xl max-h-[90vh] overflow-y-auto"
      >
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
          <h2 id="create-material-title" className="text-lg font-semibold text-gray-900">
            Create material
          </h2>
          <p className="text-sm text-gray-500 mt-0.5 break-words">From {entityLabel}</p>
        </div>

        <div className="px-4 sm:px-6 py-4 space-y-4">
          {isLoading && <p className="text-sm text-gray-500">Loading available types...</p>}

          {isError && (
            <p className="text-sm text-red-600">
              Could not load material types from PitchLess.
            </p>
          )}

          {!isLoading && !isError && types.length === 0 && (
            <p className="text-sm text-gray-600">
              No material types are available for this workspace yet.
            </p>
          )}

          {types.length > 0 && (
            <div>
              <label
                htmlFor="material-type"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Type
              </label>
              <select
                id="material-type"
                data-testid="create-material-type"
                value={typeKey}
                onChange={(e) => {
                  setTypeKey(e.target.value);
                  setConfirmDuplicate(false);
                }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">Select a type...</option>
                {types.map((t) => (
                  <option key={t.key} value={t.key}>
                    {t.displayName || t.key}
                    {existingTypeKeys.includes(t.key) ? ' (already created)' : ''}
                  </option>
                ))}
              </select>
              {typeKey !== '' && (
                <p className="text-xs text-gray-500 mt-1">
                  {types.find((t) => t.key === typeKey)?.description ?? ''}
                </p>
              )}
            </div>
          )}

          <div>
            <label htmlFor="material-title" className="block text-sm font-medium text-gray-700 mb-1">
              Title <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              id="material-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="PitchLess will name it if you leave this blank"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label htmlFor="material-brief" className="block text-sm font-medium text-gray-700 mb-1">
              What should it say? <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              id="material-brief"
              data-testid="create-material-brief"
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
              rows={3}
              placeholder="Leave blank and PitchLess will work from this record"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          {isDuplicate && !confirmDuplicate && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
              <p className="text-sm text-amber-900">
                A material of this type already exists for this record. Creating another
                generates a second document and uses credits again.
              </p>
            </div>
          )}

          {error && (
            <p className="text-sm text-red-600" role="alert">
              {error}
            </p>
          )}
        </div>

        <div className="px-4 sm:px-6 py-4 border-t border-gray-200 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 min-h-[44px] text-sm text-gray-700 hover:bg-gray-50 rounded-lg border border-gray-300"
          >
            Cancel
          </button>

          {isDuplicate && !confirmDuplicate ? (
            <button
              type="button"
              disabled={creating}
              onClick={() => setConfirmDuplicate(true)}
              className="px-4 py-2 min-h-[44px] text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg disabled:opacity-50"
            >
              Create another anyway
            </button>
          ) : (
            <button
              type="button"
              disabled={typeKey === '' || creating}
              data-testid="create-material-submit"
              onClick={() => void submit(confirmDuplicate)}
              className="px-4 py-2 min-h-[44px] text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
            >
              {creating ? 'Starting...' : 'Create material'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
